/* eslint-disable no-magic-numbers */

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import { Accordion } from 'carbon-react/lib/components/accordion';

import { FormattedMessage } from '@futrli/localisation';

import { FreestyleMethodCellData, FreestyleMethodRowData } from '../types';
import styles from './freestyle-content-row.scss';
import { FreestyleContentCell } from '../freestyle-content-cell/freestyle-content-cell';

interface FreestyleContentRowProps {
  data: FreestyleMethodRowData;
  forceExpand?: unknown;
  format?: 'currency' | 'decimal';
  isDisabled?: boolean;
  isExpanded?: boolean;
  onCollapse?: (checkedMonths: FreestyleMethodCellData[], year: FreestyleMethodRowData) => void;
  onSelectAll?: (e: React.ChangeEvent<HTMLInputElement>, year: FreestyleMethodRowData) => void;
  onSelect?: (months: FreestyleMethodCellData[], year: FreestyleMethodRowData) => void;
  onValueChange?: (value: string, month: FreestyleMethodCellData, year: FreestyleMethodRowData) => void;
  pastBlankDataValue?: string;
}

export const FreestyleContentRow = memo(
  ({
    data,
    forceExpand,
    format = 'currency',
    isDisabled = false,
    isExpanded = false,
    onSelectAll,
    onSelect,
    onValueChange
  }: FreestyleContentRowProps) => {
    const hasData = !!data.months.find(m => m.value && m.value !== '0');
    const expandedOnInit = useRef<boolean>(false);
    expandedOnInit.current = data.isCurrentYear || hasData || isExpanded;

    const restoreCheckedItems = useRef<FreestyleMethodCellData[]>([]);
    const [expanded, setExpanded] = useState(expandedOnInit.current);
    const isMounted = useRef(false);

    // ************
    // * Handlers *
    // ************

    const handleSelectAllClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const handleSelectCell = useCallback(
      (cellData: FreestyleMethodCellData) => {
        onSelect?.([cellData], data);
      },
      [data, onSelect]
    );

    const handleToggle = useCallback(() => {
      if (expanded) {
        // collapsing, remember items that were checked and then uncheck them (for now) unless 'select all' is checked
        if (!data.isAllSelected) {
          restoreCheckedItems.current = data.months.filter(m => m.selected);
          setTimeout(() => {
            onSelect?.(restoreCheckedItems.current, data);
          }, 250);
        }
      } else {
        // expanding, re-check the checkboxes that were checked when we collapsed unless 'select all' is checked
        if (!data.isAllSelected) {
          onSelect?.(restoreCheckedItems.current, data);
        }
        restoreCheckedItems.current = [];
      }
      setExpanded(!expanded);
    }, [data, expanded, onSelect]);

    const handleValueChangeCell = useCallback(
      (value: string, cellData: FreestyleMethodCellData) => {
        onValueChange?.(value, cellData, data);
      },
      [data, onValueChange]
    );

    // ***********
    // * Effects *
    // ***********

    // If mounted and the forceExpand prop changes and is truthy, force row to expand
    useEffect(() => {
      if (isMounted.current && forceExpand) {
        setExpanded(true);
      }
    }, [forceExpand]);

    // If mounted and the driverInstanceId changes, force row to expand/collapse based upon expandedOnInit.current
    useEffect(() => {
      if (isMounted.current) {
        setExpanded(expandedOnInit.current);
      }
    }, [data.driverInstanceId]);

    // After init, mark component as mounted
    useEffect(() => {
      isMounted.current = true;
    }, []);

    return (
      <div className={clsx(styles.financialYearContainer, styles.accordionContainer)} data-testid="FreestyleContentRow">
        <Accordion
          expanded={expanded}
          onChange={handleToggle}
          aria-hidden="true"
          disableContentPadding
          borders="none"
          size="small"
          title={
            <div className={styles.accordionSummary}>
              <div
                style={{ display: 'flex', alignItems: 'center' }}
                id={`financial-year-summary-${data.date}`}
                aria-controls={`financial-year-details-${data.date}`}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 1 }}>
                  <div className={styles.year}>{data.date}</div>
                </div>
                <Checkbox
                  id={`select-all-${data.date}`}
                  checked={data.isAllReadOnly ? false : data.isAllSelected}
                  onChange={e => onSelectAll?.(e, data)}
                  disabled={isDisabled || data.isAllReadOnly}
                  onClick={handleSelectAllClick}
                  mb={0}
                />
                <label
                  className={styles.selectYearLabel}
                  htmlFor={`select-all-${data.date}`}
                  onClick={handleSelectAllClick}
                >
                  <FormattedMessage id="common.select_all" defaultMessage="Select all" />
                </label>
              </div>
            </div>
          }
        >
          <div className={styles.financialYear} data-testid="yearRow">
            {data.months.map(month => (
              <FreestyleContentCell
                key={month.date}
                data={month}
                format={format}
                isDisabled={isDisabled}
                onSelect={handleSelectCell}
                onValueChange={handleValueChangeCell}
              />
            ))}
          </div>
        </Accordion>
      </div>
    );
  }
);
